import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-student-pa-kyc',
  templateUrl: './student-pa-kyc.component.html',
  styleUrls: ['./student-pa-kyc.component.css']
})
export class StudentPaKycComponent implements OnInit {
  token: string = '';
  isSubmitting: boolean = false;
  todayDate: string = new Date().toISOString().split('T')[0];

  relationships: string[] = [
    'Spouse', 'Parent', 'Child', 'Sibling', 'Guardian',
    'Friend', 'Relative', 'Other'
  ];

  kycForm: FormGroup = this.fb.group({
    emailAddress: ['', [Validators.required, Validators.email]],
    phoneNumber: ['', [Validators.required, Validators.pattern(/^0[17]\d{8}$/)]],
    nextOfKin: this.fb.array([this.buildNextOfKin()]),
  });

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private titleService: Title,
  ) {}

  ngOnInit(): void {
    this.titleService.setTitle('Student Personal Accident — Personal Details | ICEA LION');
    this.route.queryParams.subscribe(params => {
      this.token = params['token'] !== 'undefined' ? (params['token'] || '') : '';
    });
  }

  get nextOfKin(): FormArray {
    return this.kycForm.get('nextOfKin') as FormArray;
  }

  buildNextOfKin(): FormGroup {
    return this.fb.group({
      fullName: ['', Validators.required],
      relationship: ['', Validators.required],
      emailAddress: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^0[17]\d{8}$/)]],
      sharePercent: ['', [Validators.required, Validators.min(1), Validators.max(100)]],
    });
  }

  addNextOfKin(): void {
    this.nextOfKin.push(this.buildNextOfKin());
  }

  removeNextOfKin(index: number): void {
    if (this.nextOfKin.length > 1) {
      this.nextOfKin.removeAt(index);
    }
  }

  get totalSharePercent(): number {
    return this.nextOfKin.controls.reduce((sum, ctrl) => {
      return sum + (Number(ctrl.get('sharePercent')?.value) || 0);
    }, 0);
  }

  get sharePercentError(): boolean {
    return this.kycForm.touched && this.totalSharePercent !== 100;
  }

  private async sendPayloadToBackend(payload: unknown): Promise<any> {
    const url = environment.baseUrl.includes('/motor')
      ? environment.baseUrl.replace('/motor', '/pa-student-kyc')
      : `${environment.baseUrl.replace(/\/$/, '')}/pa-student-kyc`;

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const rawText = await response.text();
    try {
      return rawText ? JSON.parse(rawText) : null;
    } catch {
      return { rawText };
    }
  }

  async submitForm(): Promise<void> {
    if (this.kycForm.invalid || this.sharePercentError || this.isSubmitting) {
      this.kycForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;

    const payload = {
      token: this.token,
      personalDetails: {
        emailAddress: this.kycForm.value.emailAddress,
        phoneNumber: this.kycForm.value.phoneNumber,
      },
      nextOfKin: this.kycForm.value.nextOfKin,
    };

    try {
      await this.sendPayloadToBackend(payload);
    } catch {
      this.isSubmitting = false;
    } finally {
      window.close();
    }
  }
}
